/**
 * OfferOptionsTable Component
 * Displays offer payment plan options with recommended option highlighting
 */

import type { OfferOption } from '@/types/backoffice-crud.types';
import { Badge } from '@/components/ui/Badge';
import { formatCurrency } from '@/utils/formatters';
import { Star } from 'lucide-react';

interface OfferOptionsTableProps {
  options: OfferOption[];
}

export function OfferOptionsTable({ options }: OfferOptionsTableProps) {
  if (!options || options.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        Sin opciones de pago disponibles.
      </p>
    );
  }

  const sorted = [...options].sort((a, b) => a.term_weeks - b.term_weeks);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs text-muted-foreground">
            <th className="px-3 py-2 font-medium">Plazo</th>
            <th className="px-3 py-2 text-right font-medium">Tasa Anual</th>
            <th className="px-3 py-2 text-right font-medium">Pago Semanal</th>
            <th className="px-3 py-2 text-right font-medium">Comision Apertura</th>
            <th className="px-3 py-2 text-right font-medium">Total a Pagar</th>
            <th className="px-3 py-2 text-right font-medium">Interes Total</th>
            <th className="px-3 py-2 text-right font-medium">Ahorro</th>
            <th className="px-3 py-2 font-medium">Recomendada</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((option) => {
            const fee = option.opening_fee_mxn ?? 0;
            const feeIva = option.opening_fee_iva_mxn ?? 0;
            const totalFee = fee + feeIva;
            return (
              <tr
                key={option.term_weeks}
                className={`border-b border-border/50 ${
                  option.is_recommended
                    ? 'bg-primary/5 font-medium'
                    : ''
                }`}
              >
                <td className="px-3 py-2 text-foreground">
                  {option.term_weeks} semanas
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-foreground">
                  {(option.annual_rate * 100).toFixed(1)}%
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-foreground">
                  {formatCurrency(option.weekly_payment_mxn, 'MXN', 'es-MX')}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-foreground">
                  <span
                    title={`Comision ${formatCurrency(fee, 'MXN', 'es-MX')} + IVA ${formatCurrency(feeIva, 'MXN', 'es-MX')}${totalFee > 0 ? ' (financiada)' : ''}`}
                  >
                    {formatCurrency(totalFee, 'MXN', 'es-MX')}
                  </span>
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-foreground">
                  {formatCurrency(option.total_to_pay_mxn, 'MXN', 'es-MX')}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-foreground">
                  {formatCurrency(option.total_interest_mxn, 'MXN', 'es-MX')}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-foreground">
                  {option.savings_vs_longest > 0
                    ? `+${formatCurrency(option.savings_vs_longest, 'MXN', 'es-MX')} ahorro`
                    : '-'}
                </td>
                <td className="px-3 py-2">
                  {option.is_recommended ? (
                    <Badge variant="success">
                      <span className="flex items-center gap-1">
                        <Star size={12} />
                        {option.recommendation_reason || 'Recomendada'}
                      </span>
                    </Badge>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
